import { LibraryEntry, LibraryEntryKind, PointSequence } from './types';

/**
 * Editable copies carry a client-only `_uid`, so list rows keep a stable React key while
 * their key, label and values are being typed (never use an editable value as a key).
 */
export type DraftSequence = PointSequence & { _uid: string };
export type DraftEntry = Omit<LibraryEntry, 'meta'> & { _uid: string; meta?: { sequences?: DraftSequence[] } | null };

export const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]*$/;

let counter = 0;
export const newUid = () => `e${Date.now().toString(36)}${(counter += 1).toString(36)}`;

export const toDraft = (entries: LibraryEntry[]): DraftEntry[] =>
  entries.map(({ meta, ...entry }) => ({
    ...entry,
    _uid: newUid(),
    meta: meta?.sequences ? { ...meta, sequences: meta.sequences.map((s) => ({ ...s, _uid: newUid() })) } : null,
  }));

export const toPayload = (draft: DraftEntry[]): LibraryEntry[] =>
  draft.map(({ _uid, meta, ...entry }, index) => ({
    ...entry,
    sort_order: index + 1,
    meta: meta?.sequences ? { ...meta, sequences: meta.sequences.map(({ _uid: _s, ...s }) => s) } : meta ?? null,
  }));

export const blankEntry = (kind: LibraryEntryKind): DraftEntry => {
  const base = { _uid: newUid(), kind, is_active: true, validation: [], field_type: 'number', placeholder: '' };
  if (kind === 'points') {
    return {
      ...base,
      key: 'calibration_points',
      label: 'Calibration points',
      meta: { sequences: [{ _uid: newUid(), key: 'increasing', values: [0.2, 0.4, 0.6, 0.8, 1], range_scaled: true }] },
    };
  }
  return { ...base, key: '', label: '', meta: null };
};

/** Problems per entry uid; an empty object means the library can be saved. */
export const validateDraft = (draft: DraftEntry[]): Record<string, string[]> => {
  const problems: Record<string, string[]> = {};
  const add = (uid: string, message: string) => { (problems[uid] ||= []).push(message); };
  const counts = new Map<string, number>();
  draft.forEach((e) => counts.set(e.key, (counts.get(e.key) || 0) + 1));

  draft.forEach((e) => {
    if (!e.key) add(e._uid, 'Key is required.');
    else if (!KEY_PATTERN.test(e.key)) add(e._uid, 'Key must start with a letter and use only letters, digits, _ . -');
    else if ((counts.get(e.key) || 0) > 1) add(e._uid, 'Key must be unique in this library.');
    if (!e.label.trim()) add(e._uid, 'Label is required.');
    if (e.kind === 'points') {
      const sequences = e.meta?.sequences || [];
      if (sequences.length === 0) add(e._uid, 'Add at least one point sequence.');
      sequences.forEach((s) => {
        if (!s.key.trim()) add(e._uid, 'Every sequence needs a key.');
        if (s.values.length === 0) add(e._uid, `Sequence "${s.key || '?'}" needs at least one value.`);
      });
    }
  });
  return problems;
};

/** Parses "0.2, 0.4 0.6" into numbers; null when any part is not a number. */
export const parseValues = (text: string): number[] | null => {
  const parts = text.split(/[\s,;]+/).filter(Boolean);
  const numbers = parts.map(Number);
  return numbers.every((n) => Number.isFinite(n)) ? numbers : null;
};
